import { Typography, Stack, IconButton, Card, Box, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Modal as BaseModal } from '@mui/base/Modal';
import { useState, forwardRef } from "react";
import clsx from 'clsx';
import { styled, css } from '@mui/system';
import { t } from 'i18next';
import { THubstripeCustomer } from '../../../shared/types/hubstripeCustomer.type'
import BillingPaymentMethodsItemsTable from "../../../components/user/billings/BillingPaymentMethodsItemsTable";
import BillingPaymentMethodsForm from '../../../components/user/billings/BillingPaymentMethodsForm';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY ?? '');

type PaymentMethod = {
    id: string;
    [key: string]: any; // ou définis précisément les champs si tu veux
};

type BillingPaymentMethodsProps = {
    customer: THubstripeCustomer | null;
    paymentMethods: Record<string, PaymentMethod> | null;
    onPaymentMethodsChange: () => void;
    loadingPaymentMethods: boolean;
    onSuccess?: () => void
}

function BillingPaymentMethods({
    customer,
    paymentMethods,
    onPaymentMethodsChange,
    loadingPaymentMethods,
    onSuccess
}: BillingPaymentMethodsProps) {

    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const options: StripeElementsOptions = {
        mode: 'setup',
        currency: customer?.currency ?? 'usd',
        // Fully customizable with appearance API.
        appearance: {
            theme: 'stripe'
        },
    };

    return (
        <Card sx={{ p: 3 }}>
            {paymentMethods != null && customer != null && (
                <div>
                    <Stack spacing={2}>
                        <Box display={'flex'} gap={1} justifyContent={'space-between'} alignItems={'center'}>
                            <Typography variant="h5" color="inherit" noWrap sx={{ flexGrow: 1 }}>
                                {t("billing:payment_methods")}


                            </Typography>
                            <IconButton aria-label="add" onClick={handleOpen}>
                                <AddIcon />
                            </IconButton>

                        </Box>
                        <Divider />

                        <Modal
                            aria-labelledby="unstyled-modal-title"
                            aria-describedby="unstyled-modal-description"
                            open={open}
                            onClose={handleClose}
                            slots={{ backdrop: StyledBackdrop }}
                        >
                            <ModalContent sx={{ width: 800 }}>
                                {/* Stripe */}
                                <Elements stripe={stripePromise} options={options}>
                                    <BillingPaymentMethodsForm customer={customer} onSuccess={() => {
                                        if (typeof onSuccess === 'function') onSuccess()
                                        handleClose()
                                    }} />
                                </Elements>

                            </ModalContent>
                        </Modal>
                        <BillingPaymentMethodsItemsTable
                            customer={customer}
                            paymentMethods={paymentMethods}
                            onPaymentMethodsChange={onPaymentMethodsChange}
                            loadingPaymentMethods={loadingPaymentMethods}
                        />
                    </Stack>
                </div>
            )
            }
        </Card>
    );
}

// Define the types for Backdrop props
interface BackdropProps {
    open?: boolean;
    className?: string;
}

const Backdrop = forwardRef<HTMLDivElement, BackdropProps>(({ open, className, ...other }, ref) => {
    return (
        <div
            className={clsx({ 'base-Backdrop-open': open }, className)}
            ref={ref}
            {...other}
        />
    );
});

const grey = {
    50: '#F3F6F9',
    100: '#E5EAF2',
    200: '#DAE2ED',
    300: '#C7D0DD',
    400: '#B0B8C4',
    500: '#9DA8B7',
    600: '#6B7A90',
    700: '#434D5B',
    800: '#303740',
    900: '#1C2025',
};

const Modal = styled(BaseModal)`
    position: fixed;
    z-index: 1300;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledBackdrop = styled(Backdrop)`
    z-index: -1;
    position: fixed;
    inset: 0;
    background-color: rgb(0 0 0 / 0.5);
    -webkit-tap-highlight-color: transparent;
`;

const ModalContent = styled('div')(
    ({ theme }) => css`
        font-family: 'IBM Plex Sans', sans-serif;
        font-weight: 500;
        text-align: center;
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 8px;
        overflow: hidden;
        background-color: ${'#fff'};
        border-radius: 8px;
        border: 1px solid ${grey[200]};
        box-shadow: 0 4px 12 px${'rgb(0 0 0 / 0.2)'};
        padding: 24px;
        color: ${grey[900]};

        & .modal-title {
            margin: 0;
            line-height: 1.5rem;
            margin-bottom: 8px;
        }

        & .modal-description {
            margin: 0;
            line-height: 1.5rem;
            font-weight: 400;
            color: ${grey[800]};
            margin-bottom: 4px;
        }
    `,
);

export default BillingPaymentMethods;
